// src/components/admin/AdminInstantOrders.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import {
  Loader2, ChevronRight, ChevronDown, ChevronUp, Clock,
  User, Phone, Mail, MapPin, FileText, Package, DollarSign,
  Truck, ClipboardList, Flame, CircleCheckBig, PartyPopper, Inbox,
  Store, CalendarClock, Utensils, Hash, ShoppingBag
} from 'lucide-react';
import { supabase } from '../../services/supabase';
import { updateOrderStatus } from '../../services/orderService';

// Custom takeaway icon — person walking with bags
const TakeawayIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="3" r="2" />
    <path d="M7 21l2.5-7L7 11l3-3h4l3 3-2.5 3 2.5 7" />
    <path d="M5 16l2-1" />
    <path d="M17 16l2 1" />
    <rect x="4" y="16" width="3" height="4" rx="0.5" />
    <rect x="17" y="16" width="3" height="4" rx="0.5" />
  </svg>
);

const STATUS_COLUMNS = [
  { key: 'scheduled', label: '預約中', iconKey: 'scheduled' },
  { key: 'pending',   label: '待處理', iconKey: 'pending' },
  { key: 'preparing', label: '備餐中', iconKey: 'preparing' },
  { key: 'ready',     label: '已出餐', iconKey: 'ready' },
  { key: 'delivering', label: '配送中', iconKey: 'delivering' },
  { key: 'completed', label: '已完成', iconKey: 'completed' },
];

const STATUS_ICONS = {
  scheduled: <CalendarClock size={20} />,
  pending:   <ClipboardList size={20} />,
  preparing: <Flame size={20} />,
  ready:     <CircleCheckBig size={20} />,
  delivering: <Truck size={20} />,
  completed: <PartyPopper size={20} />,
};

const ADVANCE_MINUTES = 20;
const AUTO_ACCEPT_SECONDS = 10;
const AUTO_READY_MINUTES = 20;
const AUTO_COMPLETE_MINUTES = 5;

const isScheduledOrder = (order) => {
  return order.delivery_info?.fulfillment_mode === 'scheduled'
    && order.delivery_info?.scheduled_time;
};

const getMinutesUntilScheduled = (order, now) => {
  const scheduled = new Date(order.delivery_info.scheduled_time);
  return (scheduled - now) / 60000;
};

const formatCountdown = (minutes) => {
  if (minutes <= 0) return '即將開始';
  const totalSec = Math.floor(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
};

const getNextAction = (status, isDelivery) => {
  const map = {
    pending:    { label: '開始備餐', next: 'preparing' },
    accepted:   { label: '開始備餐', next: 'preparing' },
    preparing:  { label: '出餐完成', next: 'ready' },
    ready:      isDelivery
                  ? { label: '司機已取餐', next: 'delivering' }
                  : { label: '完成訂單', next: 'completed' },
    delivering: { label: '已完成配送', next: 'completed' },
  };
  return map[status] || null;
};

const InstantOrdersStyles = () => (
  <style>{`

    /* ===== Status Tab Bar ===== */
    .io-tab-bar {
      display: flex;
      border-bottom: 2px solid #e2e8f0;
      margin-bottom: 24px;
    }
    .io-tab-item {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 14px 0;
      cursor: pointer;
      transition: all 0.15s;
      border-bottom: 3px solid transparent;
      margin-bottom: -2px;
    }
    .io-tab-item:hover {
      background: rgba(0, 0, 0, 0.03);
    }
    .io-tab-item.active {
      border-bottom-color: #2F7D5E;
    }
    .io-tab-count {
      min-width: 24px; height: 24px;
      padding: 0 8px;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 600;
      line-height: 24px;
      text-align: center;
      display: inline-block;
    }
    .io-tab-item:not(.active) .io-tab-count {
      background: #e2e8f0;
      color: #64748b;
    }
    .io-tab-item.active .io-tab-count {
      background: #2F7D5E;
      color: white;
    }
    .io-tab-label {
      font-size: 0.95rem;
      font-weight: 500;
    }
    .io-tab-item:not(.active) .io-tab-label {
      color: #94a3b8;
    }
    .io-tab-item.active .io-tab-label {
      color: var(--saas-text-primary);
    }

    /* ===== Order Card ===== */
    .io-card-list {
      display: flex; flex-direction: column; gap: 12px;
    }
    .io-test-banner {
      background: #fef2f2;
      color: #dc2626;
      text-align: center;
      padding: 6px 12px;
      font-size: 0.82rem;
      font-weight: 600;
      border-bottom: 1px solid #fca5a5;
    }
    .io-card {
      background: white;
      border: 1px solid var(--saas-border-color);
      border-radius: 12px;
      transition: box-shadow 0.2s;
      overflow: hidden;
    }
    .io-card:hover {
      box-shadow: 0 2px 12px rgba(0,0,0,0.05);
    }

    /* Card main = icon + left info + right price/action */
    .io-card-main {
      display: flex;
      align-items: stretch;
      cursor: pointer;
    }

    /* Big type icon on the far left */
    .io-card-type-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 0 0 24px;
      flex-shrink: 0;
    }
    .io-type-icon-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
    }
    .io-type-icon-circle {
      width: 52px; height: 52px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #d1fae5;
      color: #2F7D5E;
    }
    .io-type-label {
      font-size: 0.8rem;
      font-weight: 400;
      color: #2F7D5E;
    }

    /* Left: structured info */
    .io-card-left {
      flex: 1; min-width: 0;
      padding: 20px 20px 20px 16px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    /* Top line: #order + name */
    .io-card-top {
      display: flex; align-items: center; gap: 12px;
    }
    .io-order-id {
      font-family: 'SF Mono', 'Fira Code', monospace;
      font-weight: 700; font-size: 1.1rem;
      color: #1e293b;
    }
    .io-customer-name {
      font-weight: 600; font-size: 1.1rem;
      color: var(--saas-text-primary);
    }

    /* Middle: key details — one per line */
    .io-card-details {
      display: flex; flex-direction: column; gap: 8px;
      margin-top: 8px;
    }
    .io-detail {
      display: flex; align-items: center; gap: 8px;
      font-size: 1rem; color: var(--saas-text-primary);
    }
    .io-detail-icon {
      color: #1e293b;
      flex-shrink: 0;
    }
    .io-detail-text {
      white-space: nowrap;
    }
    .io-detail-text strong {
      color: var(--saas-text-primary);
      font-weight: 600;
    }

    /* Bottom: order time */
    .io-card-time {
      display: flex; align-items: center; gap: 6px;
      font-size: 0.9rem; color: #94a3b8;
      margin-top: 6px;
    }

    /* Right: price + action + expand */
    .io-card-right {
      display: flex; flex-direction: column;
      align-items: flex-end; justify-content: center;
      padding: 20px 20px 20px 0;
      gap: 12px;
      flex-shrink: 0;
      min-width: 160px;
    }
    .io-action-btn {
      display: flex; align-items: center; gap: 6px;
      padding: 10px 22px;
      border: none; border-radius: 8px;
      font-weight: 600; font-size: 0.95rem;
      cursor: pointer; color: white;
      background: #1e293b;
      transition: all 0.2s;
      white-space: nowrap;
    }
    .io-action-btn:hover {
      background: #0f172a;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }
    .io-action-btn:disabled {
      opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none;
    }
    .io-expand-toggle {
      color: #94a3b8;
      cursor: pointer;
      transition: transform 0.2s;
    }
    .io-expand-toggle.open {
      transform: rotate(180deg);
    }

    /* ===== Expanded Detail ===== */
    .io-expanded {
      border-top: 1px solid var(--saas-border-color);
      padding: 28px;
      background: white;
    }
    .io-detail-grid {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      gap: 28px;
    }
    .io-detail-right-stack {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    .io-detail-section {
      background: white; padding: 20px;
      border-radius: 12px; border: 1px solid #e5e7eb;
    }
    .io-detail-title {
      font-size: 1.1rem; font-weight: 400;
      color: #2F7D5E;
      letter-spacing: 0.3px;
      margin-bottom: 18px;
      padding-bottom: 12px;
      border-bottom: 2px solid #1e293b;
      display: flex; align-items: center; gap: 10px;
    }
    .io-detail-title svg {
      color: #2F7D5E;
    }
    .io-detail-row {
      display: flex; justify-content: space-between;
      padding: 8px 0; font-size: 0.95rem;
    }
    .io-detail-label {
      color: var(--saas-text-primary);
      display: flex; align-items: center; gap: 8px;
    }
    .io-detail-value {
      color: var(--saas-text-primary); font-weight: 600; text-align: right;
    }
    .io-detail-row.grand {
      border-top: 2px solid #1e293b;
      padding-top: 12px; margin-top: 8px;
      font-weight: 700; font-size: 1.1rem;
    }
    .io-detail-row.grand .io-detail-value {
      color: #1e293b;
    }

    /* Item list — clean table style */
    .io-item-list {
      display: flex; flex-direction: column; gap: 0;
    }
    .io-item-row {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 0;
      border-bottom: 1px solid #f1f5f9;
    }
    .io-item-row:last-child { border-bottom: none; }
    .io-item-image {
      width: 50px; height: 50px; border-radius: 10px;
      object-fit: cover; flex-shrink: 0; border: 1px solid #e5e7eb;
    }
    .io-item-image-placeholder {
      width: 50px; height: 50px; border-radius: 10px;
      background: white; border: 1px dashed #e5e7eb;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.3rem; flex-shrink: 0;
    }
    .io-item-content { flex: 1; min-width: 0; }
    .io-item-header {
      display: flex; justify-content: space-between;
      align-items: center; margin-bottom: 2px;
    }
    .io-item-name { font-weight: 600; font-size: 1rem; color: var(--saas-text-primary); }
    .io-item-qty { font-size: 0.85rem; color: var(--saas-text-primary); margin-left: 6px; }
    .io-item-price { font-weight: 700; color: #1e293b; font-size: 1rem; white-space: nowrap; }
    .io-item-mods {
      margin-top: 4px;
    }
    .io-item-mod {
      display: flex; justify-content: space-between; padding: 2px 0;
      font-size: 1rem; color: var(--saas-text-primary);
    }
    .io-item-mod-name::before { content: "+ "; }
    .io-item-mod-price { font-weight: 600; color: #1e293b; }

    /* Weekly */
    .io-day-header {
      font-size: 0.9rem; font-weight: 700; color: var(--saas-text-primary);
      margin: 16px 0 8px; padding-bottom: 6px;
      border-bottom: 1px dashed #e2e8f0;
      display: flex; align-items: center; gap: 6px;
    }
    .io-day-header:first-child { margin-top: 0; }
    .io-meal-type-badge {
      font-size: 0.7rem; font-weight: 700; padding: 2px 8px;
      border-radius: 4px; text-transform: uppercase;
    }
    .io-meal-type-lunch { background: #fef3c7; color: #92400e; }
    .io-meal-type-dinner { background: #dbeafe; color: #1e40af; }
    .io-delivery-time-badge {
      display: inline-flex; align-items: center; gap: 3px;
      font-size: 0.7rem; color: #64748b; background: white; border: 1px solid #e2e8f0;
      padding: 2px 8px; border-radius: 4px; font-weight: 500;
    }
    .io-notes-row {
      margin-top: 10px; padding-top: 10px;
      border-top: 1px solid #e5e7eb; font-size: 0.85rem;
    }
    .io-notes-label { font-weight: 600; color: var(--saas-text-primary); margin-bottom: 4px; }
    .io-notes-value { color: var(--saas-text-primary); }

    /* ===== Auto-Accept Bar ===== */
    .io-auto-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: white;
      border: 1px solid var(--saas-border-color);
      border-radius: 12px;
      padding: 14px 20px;
      margin-bottom: 16px;
    }
    .io-auto-label {
      font-size: 0.95rem;
      font-weight: 600;
      color: #1e293b;
    }
    .io-auto-toggle {
      position: relative;
      width: 48px;
      height: 26px;
      border-radius: 13px;
      border: none;
      cursor: pointer;
      background: #cbd5e1;
      transition: background 0.2s;
      padding: 0;
    }
    .io-auto-toggle.active {
      background: #2F7D5E;
    }
    .io-auto-toggle::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: white;
      transition: transform 0.2s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
    .io-auto-toggle.active::after {
      transform: translateX(22px);
    }

    /* ===== Countdown Text ===== */
    .io-countdown-text {
      font-size: 0.85rem;
      font-weight: 600;
      color: #92400e;
      padding: 10px 18px;
      background: #fef3c7;
      border-radius: 8px;
      white-space: nowrap;
      animation: io-pulse 1.5s ease-in-out infinite;
    }
    @keyframes io-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }

    /* ===== Scheduled Tab Buttons (unified outline) ===== */
    .io-scheduled-countdown {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 0.95rem;
      font-weight: 600;
      color: #6d28d9;
      padding: 10px 22px;
      background: #ede9fe;
      border: 1px solid #c4b5fd;
      border-radius: 8px;
      white-space: nowrap;
      width: 100%;
      box-sizing: border-box;
    }
    .io-sched-btn {
      display: flex; align-items: center; justify-content: center; gap: 6px;
      padding: 10px 22px;
      border: 1px solid #2F7D5E; border-radius: 8px;
      font-weight: 600; font-size: 0.95rem;
      cursor: pointer; color: #2F7D5E;
      background: white;
      transition: all 0.2s;
      white-space: nowrap;
      width: 100%;
      box-sizing: border-box;
    }
    .io-sched-btn:hover {
      background: #f0fdf4;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }
    .io-sched-btn:disabled {
      opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none;
    }
    .io-sched-btn.pause {
      border-color: #dc2626; color: #dc2626;
    }
    .io-sched-btn.pause:hover {
      background: #fef2f2;
    }

    /* ===== 20-min Alert Modal ===== */
    .io-alert-backdrop {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.5);
      display: flex; align-items: center; justify-content: center;
      z-index: 10000;
      animation: io-fade-in 0.2s;
    }
    @keyframes io-fade-in { from { opacity: 0; } to { opacity: 1; } }
    .io-alert-modal {
      background: white; border-radius: 16px;
      padding: 32px; width: 380px; max-width: 90vw;
      text-align: center;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      animation: io-slide-up 0.25s ease-out;
    }
    @keyframes io-slide-up { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    .io-alert-icon {
      width: 56px; height: 56px; border-radius: 50%;
      background: #fef3c7; color: #d97706;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 16px;
      animation: io-ring 0.6s ease-in-out infinite alternate;
    }
    @keyframes io-ring { from { transform: rotate(-8deg); } to { transform: rotate(8deg); } }
    .io-alert-title {
      font-size: 1.2rem; font-weight: 700; color: #1e293b; margin-bottom: 8px;
    }
    .io-alert-order {
      font-family: 'SF Mono', 'Fira Code', monospace;
      font-size: 1rem; font-weight: 600; color: #2F7D5E; margin-bottom: 4px;
    }
    .io-alert-customer { font-size: 0.95rem; color: #475569; margin-bottom: 4px; }
    .io-alert-time {
      font-size: 0.9rem; color: #d97706; font-weight: 600; margin-bottom: 20px;
    }
    .io-alert-actions {
      display: flex; gap: 10px;
    }
    .io-alert-btn {
      flex: 1; padding: 12px; border-radius: 10px;
      font-weight: 600; font-size: 0.95rem; cursor: pointer;
      border: none; transition: all 0.15s;
    }
    .io-alert-btn.secondary {
      background: #f1f5f9; color: #475569;
    }
    .io-alert-btn.secondary:hover { background: #e2e8f0; }
    .io-alert-btn.primary {
      background: #2F7D5E; color: white;
    }
    .io-alert-btn.primary:hover { background: #256b4e; }

    /* Empty & Loading */
    .io-empty {
      text-align: center; padding: 60px 20px;
      color: var(--saas-text-secondary);
      background: white; border: 1px solid var(--saas-border-color);
      border-radius: 12px;
    }
    .io-empty-icon {
      margin-bottom: 16px; display: flex; justify-content: center; color: #94a3b8;
    }
    .io-loading {
      text-align: center; padding: 60px 20px; color: var(--saas-text-secondary);
    }
    .io-loading-spinner {
      animation: spin 1s linear infinite; margin: 0 auto 16px; display: block;
    }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    /* Responsive */
    @media (max-width: 768px) {
      .io-tab-bar { overflow-x: auto; }
      .io-tab-item { padding: 14px 20px; white-space: nowrap; }
      .io-card-main { flex-direction: column; }
      .io-card-right {
        flex-direction: row; align-items: center;
        justify-content: space-between;
        padding: 0 20px 16px;
        min-width: unset;
      }
      .io-action-btn { flex: 1; justify-content: center; }
      .io-detail-grid { grid-template-columns: 1fr; }
      .io-detail-right-stack { gap: 16px; }
      .io-card-details { gap: 6px; }
    }
  `}</style>
);

const AdminInstantOrders = () => {
  const { orderRefreshTrigger, autoAdvanceTrigger } = useOutletContext() || {};
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [autoAccept, setAutoAccept] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [scheduledPaused, setScheduledPaused] = useState(new Set());
  const [prepareAlert, setPrepareAlert] = useState(null); // { id, order_number, customer, scheduled_time }
  const alertedIdsRef = useRef(new Set()); // track which orders already triggered alert
  const alarmCtxRef = useRef(null);
  const alarmIntervalRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('booking_mode', 'single')
        .in('status', ['pending', 'accepted', 'preparing', 'ready', 'delivering', 'completed'])
        .order('created_at', { ascending: false });
      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      // silently ignore
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load auto_accept setting from DB on mount
  useEffect(() => {
    const loadAutoAccept = async () => {
      try {
        const { data } = await supabase
          .from('restaurant_settings')
          .select('value')
          .eq('key', 'auto_accept')
          .single();
        if (data) setAutoAccept(data.value === true);
      } catch (e) { /* ignore if table doesn't exist yet */ }
    };
    loadAutoAccept();
  }, []);

  const toggleAutoAccept = async () => {
    const next = !autoAccept;
    setAutoAccept(next);
    try {
      await supabase
        .from('restaurant_settings')
        .upsert({ key: 'auto_accept', value: next, updated_at: new Date().toISOString() });
    } catch (e) {
      // silently ignore
    }
  };

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { if (orderRefreshTrigger > 0) fetchOrders(); }, [orderRefreshTrigger, fetchOrders]);
  useEffect(() => { if (autoAdvanceTrigger > 0) fetchOrders(); }, [autoAdvanceTrigger, fetchOrders]);

  // Scroll to specific order when navigated from modal
  const scrollHandledRef = useRef(false);
  useEffect(() => {
    const targetId = location.state?.scrollToOrderId;
    if (!targetId || isLoading || !orders.length || scrollHandledRef.current) return;
    const order = orders.find(o => o.id === targetId);
    if (!order) return;
    scrollHandledRef.current = true;
    // Determine which tab this order belongs to
    const isPendingOrAccepted = order.status === 'pending' || order.status === 'accepted';
    if (isPendingOrAccepted && isScheduledOrder(order) && getMinutesUntilScheduled(order, new Date()) > ADVANCE_MINUTES) {
      setActiveTab('scheduled');
    } else if (order.status === 'accepted') {
      setActiveTab('pending');
    } else {
      setActiveTab(order.status);
    }
    setExpandedId(targetId);
    // Scroll after DOM update
    setTimeout(() => {
      const el = document.getElementById(`io-card-${targetId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    // Clear the state so refresh doesn't re-trigger
    window.history.replaceState({}, '');
  }, [location.state, isLoading, orders]);

  // Tick every second so scheduled countdown displays live seconds
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Alarm sound for 20-min alert
  const startAlarm = useCallback(() => {
    stopAlarm();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      alarmCtxRef.current = ctx;
      const playNote = (freq, start, dur) => {
        if (ctx.state === 'closed') return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq; osc.type = 'sine';
        gain.gain.setValueAtTime(0.5, start);
        gain.gain.exponentialRampToValueAtTime(0.01, start + dur);
        osc.start(start); osc.stop(start + dur);
      };
      const playMelody = () => {
        if (ctx.state === 'closed') return;
        const t = ctx.currentTime;
        [
          { f: 880, t: 0, d: 0.15 }, { f: 1047, t: 0.15, d: 0.15 },
          { f: 880, t: 0.35, d: 0.15 }, { f: 1047, t: 0.5, d: 0.15 },
          { f: 880, t: 0.7, d: 0.15 }, { f: 1047, t: 0.85, d: 0.3 },
        ].forEach(n => playNote(n.f, t + n.t, n.d));
      };
      playMelody();
      alarmIntervalRef.current = setInterval(playMelody, 2500);
    } catch (e) { /* silently ignore */ }
  }, []);

  const stopAlarm = useCallback(() => {
    if (alarmIntervalRef.current) { clearInterval(alarmIntervalRef.current); alarmIntervalRef.current = null; }
    if (alarmCtxRef.current) { alarmCtxRef.current.close().catch(() => {}); alarmCtxRef.current = null; }
  }, []);

  // Cleanup alarm on unmount
  useEffect(() => () => stopAlarm(), [stopAlarm]);

  // Detect orders entering 20-min window → trigger alert
  useEffect(() => {
    if (!orders.length) return;
    const currentNow = new Date();
    for (const order of orders) {
      if ((order.status !== 'pending' && order.status !== 'accepted')) continue;
      if (!isScheduledOrder(order)) continue;
      if (alertedIdsRef.current.has(order.id)) continue;
      const mins = getMinutesUntilScheduled(order, currentNow);
      if (mins <= ADVANCE_MINUTES && mins > 0) {
        alertedIdsRef.current.add(order.id);
        const scheduledDisplay = new Date(order.delivery_info.scheduled_time)
          .toLocaleString('zh-TW', { hour: '2-digit', minute: '2-digit' });
        setPrepareAlert({
          id: order.id,
          order_number: order.order_number,
          customer: order.contact_info?.name || '未知客戶',
          scheduled_time: scheduledDisplay,
        });
        startAlarm();
        break; // one at a time
      }
    }
  }, [orders, now, startAlarm]);

  const handleAdvanceStatusRef = useRef(null);
  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  const handleAdvanceStatus = async (orderId, nextStatus) => {
    setUpdatingId(orderId);
    try {
      const result = await updateOrderStatus(orderId, nextStatus);
      if (result.success) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: nextStatus } : o));
      } else {
        alert('更新狀態失敗：' + result.error);
      }
    } catch (err) {
      alert('更新狀態失敗：' + err.message);
    } finally {
      setUpdatingId(null);
    }
  };
  handleAdvanceStatusRef.current = handleAdvanceStatus;

  // Auto-advance logic now runs in AdminLayout (persists across all admin pages).
  // This page only handles UI display, countdowns, and manual actions.

  const scheduledPausedRef = useRef(scheduledPaused);
  scheduledPausedRef.current = scheduledPaused;

  const handlePauseScheduled = (orderId) => {
    setScheduledPaused(prev => new Set([...prev, orderId]));
  };

  const handleManualAccept = (orderId) => {
    handleAdvanceStatus(orderId, 'accepted');
    setScheduledPaused(prev => {
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  };

  const dismissAlert = () => { stopAlarm(); setPrepareAlert(null); };
  const handleAlertStartPrepare = () => {
    if (prepareAlert) {
      handleAdvanceStatus(prepareAlert.id, 'preparing');
    }
    dismissAlert();
  };

  const toggleExpand = (orderId) => {
    setExpandedId(prev => prev === orderId ? null : orderId);
  };

  // Route orders to virtual tabs
  // scheduled tab: (pending OR accepted) + scheduled order + > 20 min away
  // pending tab: pending/accepted that don't belong in scheduled
  const getVirtualTab = (order) => {
    const isPendingOrAccepted = order.status === 'pending' || order.status === 'accepted';
    if (isPendingOrAccepted && isScheduledOrder(order) && getMinutesUntilScheduled(order, now) > ADVANCE_MINUTES) {
      return 'scheduled';
    }
    if (order.status === 'accepted') return 'pending';
    return order.status;
  };

  const countByStatus = {};
  STATUS_COLUMNS.forEach(col => { countByStatus[col.key] = 0; });
  orders.forEach(o => { const tab = getVirtualTab(o); if (countByStatus[tab] !== undefined) countByStatus[tab]++; });

  const filteredOrders = orders.filter(o => getVirtualTab(o) === activeTab);

  // Sort: scheduled tab by scheduled_time ascending, pending tab by created_at descending (default)
  if (activeTab === 'scheduled') {
    filteredOrders.sort((a, b) => {
      const tA = new Date(a.delivery_info?.scheduled_time || 0);
      const tB = new Date(b.delivery_info?.scheduled_time || 0);
      return tA - tB;
    });
  }

  const getMealSummary = (order) => {
    if (!order.items_data) return '-';
    if (order.items_data.cart_items && Array.isArray(order.items_data.cart_items)) {
      return order.items_data.cart_items.map(i => i.quantity > 1 ? `${i.name} x${i.quantity}` : i.name).join(', ') || '-';
    }
    const names = [];
    Object.values(order.items_data).forEach(day => {
      if (day.lunch) names.push(day.lunch.name);
      if (day.dinner) names.push(day.dinner.name);
    });
    return names.length > 0 ? names.join(', ') : '-';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const getCalculatedDate = (createdAt, dayIndex) => {
    const date = new Date(createdAt);
    date.setDate(date.getDate() + parseInt(dayIndex));
    return date.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'short' });
  };

  const renderCartItems = (order) => {
    const items = order.items_data?.cart_items;
    if (!items || !Array.isArray(items)) return null;
    return (
      <div className="io-item-list">
        {items.map((item, idx) => {
          const modTotal = (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
          const lineTotal = ((item.price || 0) + modTotal) * (item.quantity || 1);
          return (
            <div className="io-item-row" key={idx}>
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className="io-item-image" loading="lazy" decoding="async"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : (
                <div className="io-item-image-placeholder">{item.emoji || '🍽️'}</div>
              )}
              <div className="io-item-image-placeholder" style={{ display: item.image_url ? 'none' : undefined, ...(item.image_url ? {} : { display: 'none' }) }}>
                {item.emoji || '🍽️'}
              </div>
              <div className="io-item-content">
                <div className="io-item-header">
                  <span>
                    <span className="io-item-name">{item.name}</span>
                    {item.quantity > 1 && <span className="io-item-qty">x{item.quantity}</span>}
                  </span>
                  <span className="io-item-price">${lineTotal.toFixed(2)}</span>
                </div>
                {item.modifiers && item.modifiers.length > 0 && (
                  <div className="io-item-mods">
                    {item.modifiers.map((mod, mi) => (
                      <div className="io-item-mod" key={mi}>
                        <span className="io-item-mod-name">{mod.name || mod.alternate_name}</span>
                        {mod.price > 0 && <span className="io-item-mod-price">+${mod.price.toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeeklyItems = (order) => {
    if (!order.items_data) return null;
    const entries = Object.entries(order.items_data).filter(([key]) => key !== 'cart_items');
    if (entries.length === 0) return null;
    return (
      <div className="io-item-list">
        {entries.map(([dayId, dayData]) => {
          let dateDisplay = '';
          if (dayData.date_info?.full_date) {
            dateDisplay = `${dayData.date_info.full_date} (${dayData.date_info.weekday})`;
          } else {
            dateDisplay = getCalculatedDate(order.created_at, dayId);
          }
          const lunchTime = order.delivery_schedule?.[`${dayId}_lunch`];
          const dinnerTime = order.delivery_schedule?.[`${dayId}_dinner`];
          return (
            <div key={dayId}>
              <div className="io-day-header">📅 {dateDisplay}</div>
              {dayData.lunch && (
                <div className="io-item-row">
                  {dayData.lunch.image_url ? (
                    <img src={dayData.lunch.image_url} alt={dayData.lunch.name} className="io-item-image" loading="lazy" decoding="async"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  ) : (
                    <div className="io-item-image-placeholder">{dayData.lunch.emoji || '🍽️'}</div>
                  )}
                  <div className="io-item-image-placeholder" style={{ display: dayData.lunch.image_url ? 'none' : undefined, ...(dayData.lunch.image_url ? {} : { display: 'none' }) }}>
                    {dayData.lunch.emoji || '🍽️'}
                  </div>
                  <div className="io-item-content">
                    <div className="io-item-header">
                      <span>
                        <span className="io-meal-type-badge io-meal-type-lunch">午餐</span>
                        {lunchTime && <span className="io-delivery-time-badge"><Clock size={11} /> {lunchTime}</span>}
                      </span>
                      <span className="io-item-price">${dayData.lunch.price?.toFixed(2)}</span>
                    </div>
                    <div className="io-item-name" style={{ marginTop: 4 }}>{dayData.lunch.name}</div>
                    {dayData.lunch.modifiers?.length > 0 && (
                      <div className="io-item-mods">
                        {dayData.lunch.modifiers.map((mod, mi) => (
                          <div className="io-item-mod" key={mi}>
                            <span className="io-item-mod-name">{mod.name || mod.alternate_name}</span>
                            {mod.price > 0 && <span className="io-item-mod-price">+${mod.price.toFixed(2)}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {dayData.dinner && (
                <div className="io-item-row">
                  {dayData.dinner.image_url ? (
                    <img src={dayData.dinner.image_url} alt={dayData.dinner.name} className="io-item-image" loading="lazy" decoding="async"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  ) : (
                    <div className="io-item-image-placeholder">{dayData.dinner.emoji || '🍽️'}</div>
                  )}
                  <div className="io-item-image-placeholder" style={{ display: dayData.dinner.image_url ? 'none' : undefined, ...(dayData.dinner.image_url ? {} : { display: 'none' }) }}>
                    {dayData.dinner.emoji || '🍽️'}
                  </div>
                  <div className="io-item-content">
                    <div className="io-item-header">
                      <span>
                        <span className="io-meal-type-badge io-meal-type-dinner">晚餐</span>
                        {dinnerTime && <span className="io-delivery-time-badge"><Clock size={11} /> {dinnerTime}</span>}
                      </span>
                      <span className="io-item-price">${dayData.dinner.price?.toFixed(2)}</span>
                    </div>
                    <div className="io-item-name" style={{ marginTop: 4 }}>{dayData.dinner.name}</div>
                    {dayData.dinner.modifiers?.length > 0 && (
                      <div className="io-item-mods">
                        {dayData.dinner.modifiers.map((mod, mi) => (
                          <div className="io-item-mod" key={mi}>
                            <span className="io-item-mod-name">{mod.name || mod.alternate_name}</span>
                            {mod.price > 0 && <span className="io-item-mod-price">+${mod.price.toFixed(2)}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderExpanded = (order) => {
    const isSingle = order.booking_mode === 'single';
    const di = order.delivery_info || {};
    const ci = order.contact_info || {};
    return (
      <div className="io-expanded">
        <div className="io-detail-grid">
          {/* Left: order items */}
          <div className="io-detail-section">
            <div className="io-detail-title"><Package size={22} /> 訂單內容</div>
            {isSingle ? renderCartItems(order) : renderWeeklyItems(order)}
            {order.delivery_info?.need_cutlery !== undefined && (
              <div className="io-detail-row" style={{marginTop: 8, paddingTop: 8, borderTop: '1px solid #e2e8f0'}}>
                <span className="io-detail-label"><Utensils size={13} /> 是否需要餐具</span>
                <span className="io-detail-value" style={{color: order.delivery_info.need_cutlery ? '#2F7D5E' : '#e53e3e', fontWeight: 600}}>
                  {order.delivery_info.need_cutlery ? '需要' : '不需要'}
                </span>
              </div>
            )}
          </div>

          {/* Right: fee + contact stacked */}
          <div className="io-detail-right-stack">
            <div className="io-detail-section">
              <div className="io-detail-title"><DollarSign size={22} /> 費用明細</div>
              {(order.discount > 0 || order.totals_data?.discount > 0) ? (
              <>
                <div className="io-detail-row">
                  <span className="io-detail-label">原價小計</span>
                  <span className="io-detail-value" style={{textDecoration:'line-through', color:'#94a3b8'}}>${order.totals_data?.original_subtotal?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="io-detail-row" style={{color:'#e53e3e'}}>
                  <span className="io-detail-label">{order.totals_data?.discount_label || '全單折扣'}{order.totals_data?.discount_rate ? ` (${Math.round(order.totals_data.discount_rate * 100)}折)` : ''}</span>
                  <span className="io-detail-value">-${(order.discount || order.totals_data?.discount || 0).toFixed(2)}</span>
                </div>
                <div className="io-detail-row">
                  <span className="io-detail-label">折後小計</span>
                  <span className="io-detail-value">${order.subtotal?.toFixed(2) || '0.00'}</span>
                </div>
              </>
              ) : (
              <div className="io-detail-row">
                <span className="io-detail-label">小計</span>
                <span className="io-detail-value">${order.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              )}
              {order.delivery_info?.order_type !== 'pickup' && (
              <div className="io-detail-row">
                <span className="io-detail-label">配送費</span>
                <span className="io-detail-value">{order.delivery_fee === 0 ? '免費' : `$${order.delivery_fee?.toFixed(2) || '0.00'}`}</span>
              </div>
              )}
              {(order.totals_data?.packaging_fee > 0) && (
              <div className="io-detail-row">
                <span className="io-detail-label">外賣包裝費</span>
                <span className="io-detail-value">${order.totals_data.packaging_fee.toFixed(2)}</span>
              </div>
              )}
              <div className="io-detail-row">
                <span className="io-detail-label">GST (5%)</span>
                <span className="io-detail-value">${order.gst?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="io-detail-row">
                <span className="io-detail-label">PST (7%)</span>
                <span className="io-detail-value">${order.pst?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="io-detail-row grand">
                <span className="io-detail-label">總計</span>
                <span className="io-detail-value">${order.total_amount?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
            <div className="io-detail-section">
              <div className="io-detail-title"><User size={22} /> 聯絡資料</div>
            <div className="io-detail-row">
              <span className="io-detail-label"><User size={13} /> 姓名</span>
              <span className="io-detail-value">{ci.name || '-'}</span>
            </div>
            <div className="io-detail-row">
              <span className="io-detail-label"><Phone size={13} /> 電話</span>
              <span className="io-detail-value">{ci.phone || '-'}</span>
            </div>
            <div className="io-detail-row">
              <span className="io-detail-label"><Mail size={13} /> Email</span>
              <span className="io-detail-value">{ci.email || '-'}</span>
            </div>
            {di.address && (
              <div className="io-detail-row">
                <span className="io-detail-label"><MapPin size={13} /> 地址</span>
                <span className="io-detail-value">{di.address}{di.unit ? ` ${di.unit}` : ''}</span>
              </div>
            )}
            {di.zone_name && (
              <div className="io-detail-row">
                <span className="io-detail-label">配送區域</span>
                <span className="io-detail-value">{di.zone_name}</span>
              </div>
            )}
            {(ci.notes || order.notes) && (
              <div className="io-notes-row">
                <div className="io-notes-label"><FileText size={13} style={{display:'inline',verticalAlign:'middle'}} /> 備註</div>
                <div className="io-notes-value">{ci.notes || order.notes}</div>
              </div>
            )}
          </div>
          </div>{/* close io-detail-right-stack */}
        </div>
      </div>
    );
  };

  return (
    <>
      <InstantOrdersStyles />

      {/* Auto-Accept Toggle */}
      <div className="io-auto-bar">
        <span className="io-auto-label">自動接單</span>
        <button
          className={`io-auto-toggle ${autoAccept ? 'active' : ''}`}
          onClick={toggleAutoAccept}
          aria-label="自動接單開關"
        />
      </div>

      {/* Tab Bar */}
      <div className="io-tab-bar">
        {STATUS_COLUMNS.map(col => (
          <div
            key={col.key}
            className={`io-tab-item ${activeTab === col.key ? 'active' : ''}`}
            onClick={() => setActiveTab(col.key)}
          >
            <span className="io-tab-label">{col.label}</span>
            <span className="io-tab-count">{countByStatus[col.key]}</span>
          </div>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="io-loading">
          <Loader2 size={40} className="io-loading-spinner" />
          <p>正在載入訂單...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="io-empty">
          <div className="io-empty-icon"><Inbox size={48} /></div>
          <p>目前沒有{STATUS_COLUMNS.find(c => c.key === activeTab)?.label}的訂單</p>
        </div>
      ) : (
        <div className="io-card-list">
          {filteredOrders.map(order => {
            const isExpanded = expandedId === order.id;
            const isSingle = order.booking_mode === 'single';
            const fulfillMode = order.delivery_info?.fulfillment_mode;
            const scheduledTime = order.delivery_info?.scheduled_time;
            const orderType = order.delivery_info?.order_type;
            const isDelivery = !isSingle || orderType === 'delivery';
            const action = getNextAction(order.status, isDelivery);

            // Build display values
            const modeLabel = isSingle ? '單次點餐' : '週餐訂閱';
            const typeLabel = orderType === 'pickup' ? '自取' : orderType === 'delivery' ? '配送' : null;
            const fulfillLabel = fulfillMode === 'instant' ? '即時' : fulfillMode === 'scheduled' ? '預約' : null;
            const scheduledDisplay = (isSingle && fulfillMode === 'scheduled' && scheduledTime)
              ? new Date(scheduledTime).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
              : null;

            const isTestOrder = order.contact_info?.stripe_payment_intent_id?.startsWith('dev_test_');

            return (
              <div className={`io-card ${isExpanded ? 'expanded' : ''}`} key={order.id} id={`io-card-${order.id}`}>
                {isTestOrder && (
                  <div className="io-test-banner">#{order.order_number} 測試訂單 — 請勿出餐</div>
                )}
                <div className="io-card-main" onClick={() => toggleExpand(order.id)}>

                  {/* Big type icon */}
                  <div className="io-card-type-icon">
                    <div className="io-type-icon-wrapper">
                      <div className="io-type-icon-circle">
                        {(!isSingle || orderType === 'delivery')
                          ? <Truck size={26} />
                          : <ShoppingBag size={26} />
                        }
                      </div>
                      <span className="io-type-label">
                        {(!isSingle || orderType === 'delivery') ? '配送訂單' : '自取訂單'}
                      </span>
                    </div>
                  </div>

                  {/* Left: structured info */}
                  <div className="io-card-left">
                    <div className="io-card-details">
                      <div className="io-detail">
                        <Hash size={16} className="io-detail-icon" />
                        <span className="io-detail-text">{order.order_number}</span>
                      </div>
                      <div className="io-detail">
                        <User size={16} className="io-detail-icon" />
                        <span className="io-detail-text">{order.contact_info?.name || '未知客戶'}</span>
                      </div>
                      {typeLabel && (
                        <div className="io-detail">
                          {orderType === 'pickup'
                            ? <Store size={16} className="io-detail-icon" />
                            : <Truck size={16} className="io-detail-icon" />
                          }
                          <span className="io-detail-text">
                            {orderType === 'pickup'
                              ? (fulfillMode === 'scheduled' ? '預約自取' : '立即自取')
                              : (fulfillMode === 'scheduled' ? '預約配送' : '立即配送')
                            }
                          </span>
                        </div>
                      )}
                      {fulfillMode === 'scheduled' && scheduledDisplay && (
                        <div className="io-detail">
                          <CalendarClock size={16} className="io-detail-icon" />
                          <span className="io-detail-text">
                            {orderType === 'pickup' ? '預約取餐時間' : '預約配送時間'}：{scheduledDisplay}
                          </span>
                        </div>
                      )}
                    </div>

                      <div className="io-detail">
                        <DollarSign size={16} className="io-detail-icon" />
                        <span className="io-detail-text">${order.total_amount?.toFixed(2) || '0.00'}</span>
                      </div>

                    <div className="io-card-time">
                      <Clock size={12} />
                      下單時間 {formatTime(order.created_at)}
                    </div>
                  </div>

                  {/* Right: action + expand */}
                  <div className="io-card-right">
                    {activeTab === 'scheduled' ? (
                      <>
                        <span className="io-scheduled-countdown">
                          <CalendarClock size={16} />
                          距離預約 {formatCountdown(getMinutesUntilScheduled(order, now))}
                        </span>
                        {(() => {
                          const remaining = Math.max(0, AUTO_ACCEPT_SECONDS - Math.floor((now - new Date(order.created_at)) / 1000));
                          if (order.status === 'accepted' || (!scheduledPaused.has(order.id) && remaining <= 0)) {
                            return (
                              <span className="io-sched-btn" style={{ cursor: 'default' }}>
                                <CircleCheckBig size={16} /> 已接單
                              </span>
                            );
                          }
                          if (scheduledPaused.has(order.id)) {
                            return (
                              <button className="io-sched-btn"
                                onClick={(e) => { e.stopPropagation(); handleManualAccept(order.id); }}>
                                接單 <ChevronRight size={16} />
                              </button>
                            );
                          }
                          return (
                            <>
                              <span className="io-sched-btn" style={{ cursor: 'default' }}>
                                將於 {remaining} 秒後自動接單
                              </span>
                              <button className="io-sched-btn pause"
                                onClick={(e) => { e.stopPropagation(); handlePauseScheduled(order.id); }}>
                                暫停接單
                              </button>
                            </>
                          );
                        })()}
                        {getMinutesUntilScheduled(order, now) <= ADVANCE_MINUTES && (
                          <button
                            className="io-sched-btn"
                            disabled={updatingId === order.id}
                            onClick={(e) => { e.stopPropagation(); handleAdvanceStatus(order.id, 'preparing'); }}
                          >
                            {updatingId === order.id ? '處理中...' : '開始備餐'}
                            {updatingId !== order.id && <ChevronRight size={16} />}
                          </button>
                        )}
                      </>
                    ) : action && autoAccept && order.status === 'pending' && (() => {
                      const rem = Math.max(0, AUTO_ACCEPT_SECONDS - Math.floor((now - new Date(order.created_at)) / 1000));
                      return rem > 0;
                    })() ? (
                      <span className="io-countdown-text">
                        將於 {Math.max(0, AUTO_ACCEPT_SECONDS - Math.floor((now - new Date(order.created_at)) / 1000))} 秒後自動開始備餐
                      </span>
                    ) : action && (
                      <>
                        {order.status === 'preparing' && order.preparing_at && (() => {
                          const remainMin = AUTO_READY_MINUTES - (now - new Date(order.preparing_at)) / 60000;
                          if (remainMin <= 0) return null;
                          return (
                            <span className="io-countdown-text">
                              {formatCountdown(remainMin)} 後自動出餐
                            </span>
                          );
                        })()}
                        {order.status === 'ready' && order.ready_at && !isDelivery && (() => {
                          const remainMin = AUTO_COMPLETE_MINUTES - (now - new Date(order.ready_at)) / 60000;
                          if (remainMin <= 0) return null;
                          return (
                            <span className="io-countdown-text">
                              {formatCountdown(remainMin)} 後自動完成
                            </span>
                          );
                        })()}
                        <button
                          className="io-action-btn"
                          disabled={updatingId === order.id}
                          onClick={(e) => { e.stopPropagation(); handleAdvanceStatus(order.id, action.next); }}
                        >
                          {updatingId === order.id ? '處理中...' : action.label}
                          {updatingId !== order.id && <ChevronRight size={16} />}
                        </button>
                      </>
                    )}
                    <div className={`io-expand-toggle ${isExpanded ? 'open' : ''}`}>
                      <ChevronDown size={18} />
                    </div>
                  </div>

                </div>

                {isExpanded && renderExpanded(order)}
              </div>
            );
          })}
        </div>
      )}

      {/* 20-min Prepare Alert Modal */}
      {prepareAlert && (
        <div className="io-alert-backdrop" onClick={dismissAlert}>
          <div className="io-alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="io-alert-icon"><CalendarClock size={28} /></div>
            <div className="io-alert-title">預約訂單即將到時間！</div>
            <div className="io-alert-order">#{prepareAlert.order_number}</div>
            <div className="io-alert-customer">{prepareAlert.customer}</div>
            <div className="io-alert-time">預約時間 {prepareAlert.scheduled_time}（剩餘不到 20 分鐘）</div>
            <div className="io-alert-actions">
              <button className="io-alert-btn secondary" onClick={dismissAlert}>知道了</button>
              <button className="io-alert-btn primary" onClick={handleAlertStartPrepare}>開始備餐</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminInstantOrders;
